import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { GlobalSearchBox } from "./GlobalSearchBox";
import { getHeaderTabs } from "./headerTabs";
import { getHeaderBreadcrumbs } from "./headerBreadcrumbs";
import { useHeaderSlotRef, useHeaderRecordName, useHeaderSlotOccupied } from "./HeaderSlot";
import ProfileMenu from "./ProfileMenu";
import { useAssistant } from "../../views/assistant/useAssistant";
import AnalysisPanel from "../../views/assistant/AnalysisPanel";
import { fetchAssistantCapability } from "../../views/assistant/api";
import { usePageContext } from "../../context/PageContext";

export default function TopBar() {
	const { keycloak } = useKeycloak();
	const location = useLocation();
	const params = useParams();
	const tabs = getHeaderTabs(location, params);
	const recordName = useHeaderRecordName();
	const crumbs = getHeaderBreadcrumbs(location, params, { recordName });

	// A detail/record view is never a tab (headerTabs marks it `detail`); the
	// record's identity lives in the title instead. The tab strip only earns its
	// own row when it offers a real choice — two or more navigable sections —
	// so a lone tab (or a list+detail pair on a record page) shows no strip.
	const visibleTabs = tabs.filter((t) => !t.detail);
	const showTabRow = visibleTabs.length > 1;

	const navigate = useNavigate();
	// Parent/detail screens (a record: /settings/users/:id, /budgets/:id, an
	// investigation, an approval, …) carry an *active* `detail` tab. On those, show
	// a back chevron next to the title that returns to the parent list. The parent
	// url is the first real (non-detail) tab, else the parent breadcrumb, else
	// browser-back. List/section pages have no active detail tab → no chevron.
	const onDetailScreen = tabs.some((t) => t.detail && t.active);
	const parentUrl =
		(tabs.find((t) => !t.detail && t.url && t.url !== location.pathname) || {}).url ||
		(crumbs.length >= 2 ? crumbs[crumbs.length - 2].url : null) ||
		null;
	const goBackToParent = () => (parentUrl ? navigate(parentUrl) : navigate(-1));

	const titleRef = useHeaderSlotRef("title");
	const actionsRef = useHeaderSlotRef("actions");
	const tabStripRef = useHeaderSlotRef("tabstrip");
	const row2LeftRef = useHeaderSlotRef("row2-left");
	const row2RightRef = useHeaderSlotRef("row2-right");
	const row3Ref = useHeaderSlotRef("row3-filters");
	// When a page injects its own title, suppress the derived fallback. Otherwise
	// derive it: the breadcrumb tail names the page, or a lone tab's label does.
	const titleInjected = useHeaderSlotOccupied("title");
	const derivedTitle = titleInjected
		? null
		: (crumbs.length ? crumbs[crumbs.length - 1].label : null)
			|| (visibleTabs.length === 1 ? visibleTabs[0].title : null);
	// Row 2 exists for the tab strip AND/OR the page's context controls (period,
	// comparison, toggles). It renders whenever either is present — so a page
	// with no tab strip still shows its controls on their own row below row 1,
	// rather than crowding them into the identity row.
	const row2LeftOccupied = useHeaderSlotOccupied("row2-left");
	const row2Occupied = useHeaderSlotOccupied("row2-right");
	// A page can inject its own tab strip (data-dependent sub-tabs the route-based
	// getHeaderTabs can't express, e.g. a board report's Overview/Runs/Settings).
	const tabStripOccupied = useHeaderSlotOccupied("tabstrip");
	const showRow2 = showTabRow || tabStripOccupied || row2LeftOccupied || row2Occupied;
	const noop = () => {};

	const assistant = useAssistant();
	// Start from the last-known capability (default: enabled) rather than false,
	// so the box paints straight into ask mode instead of flashing the old
	// search box for the moment the capability check is in flight. The async
	// check below corrects + remembers it. Only an explicit `enabled: false`
	// falls back to the search box.
	const [askEnabled, setAskEnabled] = useState(() => {
		try { return window.localStorage.getItem("instantview.assistant.capable") !== "false"; }
		catch { return true; }
	});
	const pageContext = usePageContext();

	useEffect(() => {
		let alive = true;
		fetchAssistantCapability().then((c) => {
			if (!alive) return;
			setAskEnabled(!!c.enabled);
			try { window.localStorage.setItem("instantview.assistant.capable", c.enabled ? "true" : "false"); }
			catch { /* localStorage unavailable — best-effort */ }
		}).catch(() => {});
		return () => { alive = false; };
	}, []);

	// route stays sourced from useLocation (unchanged); period/scope are a
	// best-effort hint from the page-context provider mounted around Main —
	// see src/context/PageContext.jsx.
	const onAsk = (question) => assistant.ask(question, { route: location.pathname, period: pageContext.period, scope: pageContext.scope });

	return (
		<div className="shrink-0 bg-surface border-b border-solid border-card-bd">
			{/* ---------- ROW 1: breadcrumbs + identity/actions ---------- */}
			<div className="px-6 pt-3 pb-3">
				{crumbs.length > 0 && (
					<nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[12px] mb-1.5">
						{crumbs.map((c, i) => {
							const last = i === crumbs.length - 1;
							return (
								<React.Fragment key={c.label + i}>
									{i > 0 && <span className="text-[#C6CDD8]">/</span>}
									{c.url && !last ? (
										<Link to={c.url} className="text-text-3 hover:text-text-2 no-underline">{c.label}</Link>
									) : (
										<span className={last ? "text-text-2 font-semibold" : "text-text-3"}>{c.label}</span>
									)}
								</React.Fragment>
							);
						})}
					</nav>
				)}
				<div className="flex items-center gap-3">
					{/* Back-to-parent chevron on record/detail screens (see onDetailScreen).
					    Bracket sizing + explicit inline-flex/box-border/border-solid because
					    this theme zeroes the width/height scale and a global button all:unset
					    reset strips display/border from every button. */}
					{onDetailScreen && (
						<button
							type="button"
							onClick={goBackToParent}
							aria-label="Back to list"
							title="Back"
							className="-ml-1 box-border inline-flex w-[32px] h-[32px] items-center justify-center rounded-[9px] border border-solid border-transparent text-text-2 hover:text-text hover:bg-surface-2 focus-visible:outline-none focus-visible:border-card-bd cursor-pointer transition-colors"
						>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<path d="M15 18l-6-6 6-6" />
							</svg>
						</button>
					)}
					{/* Page title + status: page injects here, else a derived fallback. */}
					<div className="flex items-center gap-2.5 min-w-0" style={{ minHeight: 36 }}>
						<div ref={titleRef} className="flex items-center gap-2.5 min-w-0 empty:hidden" />
						{derivedTitle && (
							<h1 className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
								{derivedTitle}
							</h1>
						)}
					</div>
					<div className="flex-1" />
					{/* Utility (Ask) precedes the primary action (page buttons); the
					    avatar is last, set a little further apart. 8px within the action
					    group, 12px before the avatar. */}
					<div className="flex items-center gap-3">
						<div className="flex items-center gap-2">
							{/* Ask launcher — utility action, before the primary buttons. */}
							<GlobalSearchBox
								token={keycloak?.token}
								placeholder={askEnabled ? "Ask" : "Search"}
								mode={askEnabled ? "ask" : "search"}
								widthClassName="w-[118px] focus-within:w-[320px] duration-150"
								onSelectTerm={noop}
								onAsk={onAsk}
								recentQuestions={assistant.recentQuestions}
							/>
							{/* Page action buttons inject here. */}
							<div ref={actionsRef} className="flex items-center gap-2 empty:hidden" />
						</div>
						<ProfileMenu />
					</div>
				</div>
			</div>

			{/* ---------- ROW 2: tabs + context controls ---------- */}
			{/* Shown when there are ≥2 tabs OR the page has context controls, so a
			tabless page still shows its period/comparison/toggles on their own row.
			The container is always rendered (just hidden when empty) so the
			row2-right portal target exists from first paint — the page's controls
			never flash inline before landing here. */}
			{/* A thin bottom border runs across the whole row; the active tab's
			    underline sits directly on it (row 3, when present, adds no top
			    border so the line stays 1px). */}
			<div className={showRow2 ? "px-6 flex items-center gap-4 border-t border-b border-solid border-card-bd" : "hidden"} style={{ height: 46 }}>
				{showTabRow && (
					// h-full is a no-op under this theme's height scale (see project
					// notes) — size the tab track + tabs with inline 100% so each tab
					// fills the row and its underline reaches the bottom border. The
					// -ml-4 cancels the first tab's px-4 so its label lines up with the
					// row-1 title/breadcrumb (both at the px-6 content inset).
					<div className="-ml-4 flex items-stretch gap-1" style={{ height: "100%" }}>
						{visibleTabs.map((t) => (
							<Link
								key={t.url + t.title}
								to={t.disabled ? "#" : t.url}
								onClick={t.disabled ? (e) => e.preventDefault() : undefined}
								tabIndex={t.disabled ? -1 : undefined}
								aria-disabled={t.disabled || undefined}
								aria-current={t.active ? "page" : undefined}
								style={{ height: "100%" }}
								className={`relative inline-flex items-center px-4 text-[13.5px] no-underline whitespace-nowrap transition-colors focus-visible:outline-none ${
									t.active
										? "text-text font-semibold after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[3px] after:rounded-t after:bg-primary"
										: t.disabled
										? "text-[#C6CDD8] font-medium cursor-not-allowed"
										: "text-text-3 font-medium hover:text-text-2"
								}`}
							>
								{t.title}
							</Link>
						))}
					</div>
				)}
				{/* Page-provided tab strip: a direct child of the row (like the native strip
				    above) so its tabs fill the row height and the active underline lands on
				    the bottom border. Used for data-dependent sub-tabs — e.g. a board
				    report's Overview / Runs / Settings — that getHeaderTabs can't express. */}
				<div ref={tabStripRef} className="-ml-4 flex items-stretch gap-1 empty:hidden" style={{ height: "100%" }} />
				{/* Left-aligned context controls (tabless pages, e.g. cost analyzer) inject here. */}
				<div ref={row2LeftRef} className="flex items-center gap-1.5 empty:hidden" />
				{/* Period + high-level selections inject here, right-aligned. */}
				<div ref={row2RightRef} className="ml-auto flex items-center gap-1.5 empty:hidden" />
			</div>

			{/* ---------- ROW 3: filter bar (analysis pages only) ---------- */}
			{/* No top border: row 2 already carries the bottom border above it. */}
			<div
				ref={row3Ref}
				className="px-6 py-2.5 bg-surface flex items-center gap-3 flex-wrap empty:hidden"
			/>

			<AnalysisPanel controller={assistant} />
		</div>
	);
}
